import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import styles from './viewClient.module.css';
import { useHasPermission } from '../../../contexts/auth';
import { useBreadcrumbs } from '../../../components/ui/breadcrumps/breadcrumbsContext';
import { useCRUD } from '../../../api/crud';
import { CLIENT_CONTRACT_TERMS_ENDPOINT, CLIENTS_ENDPOINT, CONTRACTS_ENDPOINT, SETTLEMENTS_ENDPOINT } from '../../../consts';
import { useListCount } from '../../../hooks/useListCount';
import { TabCountBadge } from '../../../components/ui/tabCountBadge/tabCountBadge.component';

export const ViewClient = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { setOverride } = useBreadcrumbs();
  const { get } = useCRUD(CLIENTS_ENDPOINT);
  const canViewContracts = useHasPermission("settlement.view_contract");
  const canViewSettlements = useHasPermission("settlement.view_settlement");
  const canViewEquipments = useHasPermission("settlement.view_equipment");
  const canViewPortalAccess = useHasPermission("portal.view_clientportalcontact");
  const canViewInvoices = useHasPermission("invoice.view_invoice");

  // Fetch client data for breadcrumb override
  useEffect(() => {
    const fetchClient = async () => {
      if (id) {
        const response = await get(id);
        setOverride(`/clients/${id}`, response.data ? (response.data.short_name || response.data.name) : "");
      }
    };
    fetchClient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, get]);

  // Extract client ID from pathname
  const pathname = location.pathname;
  const clientIdMatch = pathname.match(/\/clients\/([^/]+)/);
  const clientId = clientIdMatch ? clientIdMatch[1] : null;
  const basePath = clientId ? `/clients/${clientId}` : '/clients';

  const contractsCount = useListCount(
    clientId ? `${CONTRACTS_ENDPOINT}?client=${clientId}` : null,
    canViewContracts
  );
  const settlementsCount = useListCount(
    clientId ? `${SETTLEMENTS_ENDPOINT}monthly-summary/?client=${clientId}` : null,
    canViewSettlements
  );
  const activeEquipmentCount = useListCount(
    clientId ? `${CLIENT_CONTRACT_TERMS_ENDPOINT}?client_id=${clientId}&is_active=true` : null,
    canViewEquipments
  );
  const expiredEquipmentCount = useListCount(
    clientId ? `${CLIENT_CONTRACT_TERMS_ENDPOINT}?client_id=${clientId}&is_active=false` : null,
    canViewEquipments
  );
  const tabCounts: Record<string, number | undefined> = {
    contracts: contractsCount,
    settlements: settlementsCount,
    'active-equipment': activeEquipmentCount,
    'expired-equipment': expiredEquipmentCount,
  };

  const visibleTabs = [
    { key: 'basic', path: basePath },
    ...(canViewContracts ? [{ key: 'contracts', path: `${basePath}/contracts` }] : []),
    ...(canViewSettlements ? [{ key: 'settlements', path: `${basePath}/settlements` }] : []),
    ...(canViewEquipments ? [
      { key: 'active-equipment', path: `${basePath}/active-equipment` },
      { key: 'expired-equipment', path: `${basePath}/expired-equipment` }
    ] : []),
    ...(canViewPortalAccess ? [{ key: 'portal-access', path: `${basePath}/portal-access` }] : []),
    ...(canViewInvoices ? [{ key: 'invoice-settings', path: `${basePath}/invoice-settings` }] : []),
  ];

  const getTabValue = () => {
    if (pathname.includes('/invoice-settings')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'invoice-settings');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/portal-access')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'portal-access');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/settlements')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'settlements');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/expired-equipment')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'expired-equipment');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/active-equipment')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'active-equipment');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/contracts') && !pathname.endsWith('/contracts')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'contracts');
      return index >= 0 ? index : 0;
    }
    if (pathname.includes('/contracts')) {
      const index = visibleTabs.findIndex(tab => tab.key === 'contracts');
      return index >= 0 ? index : 0;
    }
    return 0;
  };

  return (
    <Box className={styles.container}>
      <Tabs
        value={getTabValue()}
        variant="scrollable"
        scrollButtons="auto"
      >
        {visibleTabs.map((tab) => {
          const labels: Record<string, string> = {
            basic: t("Basic Information"),
            contracts: t("Contracts"),
            settlements: t("Settlements"),
            "active-equipment": t("Active equipment"),
            "expired-equipment": t("Previously used equipment"),
            "portal-access": t("Portal access"),
            "invoice-settings": t("Invoice defaults"),
          };
          return (
            <Tab
              key={tab.key}
              label={<TabCountBadge label={labels[tab.key]} count={tabCounts[tab.key]} />}
              onClick={() => navigate(tab.path)}
            />
          );
        })}
      </Tabs>
      <Box className={styles.content}>
        <Outlet />
      </Box>
    </Box>
  );
};
